import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { resetClientStores } from '@stores/resetClientStores.js'

// Treat the token as expired a few seconds early to cover clock skew between the
// browser and the finder that minted the expiry.
const CLOCK_SKEW_MS = 5000

export const useAuthStore = defineStore('authStore', () => {
  const token = ref(null)
  const expiresAt = ref(null)
  const client = ref(null)

  const isAuthenticated = computed(() => {
    return !!token.value && !!expiresAt.value && new Date(expiresAt.value) > new Date()
  })

  const isExpired = computed(() => {
    if (!expiresAt.value) return false
    return new Date(expiresAt.value).getTime() <= Date.now() + CLOCK_SKEW_MS
  })

  const setSession = (data) => {
    token.value = data.token
    expiresAt.value = data.expires_at
    client.value = data.client
  }

  const clearSession = async () => {
    const currentToken = token.value

    // Clear local state first so the UI logs out immediately, even if the
    // request to revoke the server-side token fails.
    token.value = null
    expiresAt.value = null
    client.value = null

    if (!currentToken) {
      return
    }

    // Best-effort revoke of the Sanctum token server-side, retried once so a
    // transient failure does not leave a live Bearer token alive on the server
    // for the rest of its multi-day expiry.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        await axios.post('/api/client/auth/logout', {}, {
          headers: { Authorization: `Bearer ${currentToken}` },
        })

        return
      } catch {
        // Retry once, then give up: local state is already cleared.
      }
    }
  }

  // Proactively enforce the token TTL: if the stored token has reached its
  // expiry, log out locally (revoke server-side, best effort) and wipe the
  // draft stores. Nothing else clears an expired token — there is no refresh
  // endpoint, so the user re-authenticates via a fresh magic link.
  const enforceExpiry = () => {
    if (token.value && isExpired.value) {
      clearSession()
      resetClientStores()

      return true
    }

    return false
  }

  return {
    token,
    expiresAt,
    client,
    isAuthenticated,
    isExpired,
    setSession,
    clearSession,
    enforceExpiry,
  }
  // Persist ONLY the token and its expiry — never the client PII blob (name,
  // email, …). The client object is refetched from the finder on load; keeping
  // it out of localStorage means PII is not left at rest in the browser.
}, { persist: { paths: ['token', 'expiresAt'] } })
